<template>
    <Drawer v-model:visible="visible" position="bottom" style="height:auto;" header="test" @update:visible="onHide"
        appendTo="body">

        <template #header>
            <div class="flex items-center gap-2 p-drawer-title">
                <i class="pi pi-check-circle text-green-500" />
                <span>{{ $t('yourSummary') }}</span>
            </div>
        </template>
        <ScrollPanel style="width: 100%; height: 50vh">
            <div class="rounded-lg p-4 mb-4 text-white" :class="getUserGradientByUsername(user?.username || '')">
                <div class="flex items-center justify-between">
                    <div>
                        <div class="text-sm opacity-90">{{ $t('performedChores.totalXpEarned') }}</div>
                        <div class="text-2xl font-bold">{{ totalUserXp }} XP</div>
                    </div>
                    <div class="text-right">
                        <div class="text-sm opacity-90">{{ $t('performedChores.choresCompleted') }}</div>
                        <div class="text-xl font-semibold">{{ performedChoreSummary?.performedChores?.length }}</div>
                    </div>
                </div>
            </div>
            <!-- Level Updates Section -->
            <div class="space-y-4 mb-6" v-if="mainLevelUpdate || categoryLevelUpdates.length > 0">
                <!-- Main Level -->
                <div v-if="mainLevelUpdate">
                    <LevelUpdateCard :levelUpdate="mainLevelUpdate" showAnimation
                        :text="getMainLevelName(mainLevelUpdate.newLevel!)" />
                </div>

                <div v-if="categoryLevelUpdates.length > 0" class="space-y-3">
                    <div class="text-sm font-medium text-slate-700 dark:text-slate-300">
                        {{ $t('categoryLevels') }}
                    </div>
                    <div class="grid gap-3 sm:grid-cols-2">
                        <LevelUpdateCard v-for="lu in categoryLevelUpdates" :key="lu.categoryId!"
                            :levelUpdate="lu.levelUpdate!" showAnimation
                            :text="getCategoryRankName(lu.levelUpdate!.newLevel!, lu.categoryId!, lu.categoryName!)" />
                    </div>
                </div>
            </div>

            <div>
                <div class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                    {{ $t('xpDetails') }}
                </div>
                <div class="grid gap-3 sm:grid-cols-2">
                    <Card v-for="chore in performedChoreSummary?.performedChores" :key="chore.id!"
                        class="!shadow-md hover:!shadow-lg transition-shadow duration-200 border-gray-200! dark:border-gray-700! border-1">
                        <template #title>
                            <div class="flex items-center justify-between">
                                <span>{{ chore.choreName }}</span>
                                <div class="flex items-center gap-1">
                                    <i class="pi pi-star-fill text-yellow-500"></i>
                                    <span class="font-bold text-lg">{{ getTotalChoreXP(chore) }}</span>
                                </div>
                            </div>
                        </template>

                        <template #content>
                            <div class="space-y-2">
                                <div v-for="participant in chore.participants" :key="participant.userId!"
                                    class="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700 rounded">
                                    <span class="font-medium">{{ participant.username }}</span>
                                    <div class="flex items-center gap-2">
                                        <div v-if="participant.bonusXpAwarded"
                                            class="text-xs text-slate-600 dark:text-slate-400">
                                            {{ participant.baseXpAwarded }} + {{ participant.bonusXpAwarded }}
                                            {{ $t("bonus") }}
                                        </div>
                                        <span class="font-bold text-green-600 dark:text-green-400">
                                            {{ participant.totalXpAwarded }} XP
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </Card>
                </div>
            </div>
        </ScrollPanel>
    </Drawer>
</template>

<script setup lang="ts">
import { useTaskBoardStore, useUsersStore } from "@/stores";
import { computed } from "vue";
import { storeToRefs } from "pinia";
import LevelUpdateCard from "@/components/level-up/LevelUpdateCard.vue";
import { useLevelNamer } from "@/composables/level-namer";
import { useUserColors } from "@/composables/user-colors";
import type { PerformedChoreDto } from "@/client";
const { getUserGradientByUsername } = useUserColors();

const { getMainLevelName, getCategoryRankName } = useLevelNamer();
const visible = computed(() => {
    return performedChoreSummary.value != null;
});
const { performedChoreSummary } = storeToRefs(useTaskBoardStore());
const { user } = storeToRefs(useUsersStore());

const userXpUpdate = computed(() => {
    return performedChoreSummary.value?.userXpUpdates?.find(u => u.userId === user.value?.id);
});

const mainLevelUpdate = computed(() => {
    return userXpUpdate.value?.mainLevelUpdate;
});

const categoryLevelUpdates = computed(() => {
    return userXpUpdate.value?.categoryLevelUpdates || [];
});

const totalUserXp = computed(() => {
    return performedChoreSummary.value?.performedChores?.flatMap(c => c.participants || [])
        .filter(p => p.userId === user.value?.id)
        .reduce((acc, p) => acc + (p.totalXpAwarded || 0), 0) || 0;
})

const getTotalChoreXP = (chore: PerformedChoreDto) => {
    return chore.participants?.reduce((acc, p) => acc + (p.totalXpAwarded || 0), 0) || 0;
};

const onHide = () => {
    performedChoreSummary.value = null;
};

</script>
